/**
 * Composable for reactive browser adapter management
 *
 * This composable provides a reactive wrapper around browser adapter instances,
 * ensuring proper Vue reactivity for adapter state changes and cleanup on unmount.
 *
 * Key features:
 * - Reactive state management for adapter data
 * - Automatic cleanup on component unmount
 * - Type-safe adapter operations
 * - Support for all browser adapter types
 */

import {
  ref,
  computed,
  onUnmounted,
  type Ref,
  type ComputedRef,
  shallowRef,
  toValue,
} from 'vue';
import type { BaseBrowserAdapter } from '../adapters/BaseBrowserAdapter';
import type { BrowserContext } from '../types/browser-interfaces';

/**
 * Reactive adapter state
 */
export interface ReactiveAdapterState<T> {
  // Core state refs from adapter's reactive state
  items: Ref<T[]>;
  filteredItems: Ref<T[]>;
  selectedItems: Ref<T[]>;
  focusedItem: Ref<T | null>;
  searchQuery: Ref<string>;
  isLoading: Ref<boolean>;
  isInitialized: Ref<boolean>;
  error: Ref<string | null>;
  hasMore: Ref<boolean>;
  page: Ref<number>;

  // Computed state
  selectedIds: ComputedRef<Set<string>>;
  hasSelection: ComputedRef<boolean>;
  itemCount: ComputedRef<number>;

  // Methods
  initialize: (params?: BrowserContext) => Promise<void>;
  refresh: () => Promise<void>;
  search: (query: string) => void;
  selectItem: (item: T) => void;
  toggleItem: (item: T) => void;
  clearSelection: () => void;
  selectAll: () => void;
  setSelectedIds: (ids: string[] | Set<string>) => void;
  dispose: () => void;
}

/**
 * Composable for managing browser adapter with reactive state
 *
 * @param adapter - The browser adapter instance
 * @param autoInitialize - Whether to automatically initialize on mount
 * @returns Reactive adapter state and methods
 */
export function useBrowserAdapter<T>(
  adapter: BaseBrowserAdapter<T>,
  autoInitialize = true
): ReactiveAdapterState<T> {
  // Use shallowRef for the adapter to avoid deep reactivity on the class instance
  const adapterRef = shallowRef(adapter);

  // Get reactive state directly from adapter - no polling needed!
  const reactiveState = adapterRef.value.getReactiveState();

  // Additional state for initialization tracking
  const isInitialized = ref(false);

  // Computed state based on adapter's reactive state
  const selectedIds = computed(() => {
    const ids = new Set<string>();
    const selectedItems = toValue(reactiveState.selectedItems);
    if (!selectedItems) return ids;

    selectedItems.forEach((item) => {
      const itemId = adapterRef.value.itemConfig.getId(item);
      if (itemId) ids.add(itemId);
    });
    return ids;
  });

  const hasSelection = computed(() => selectedIds.value.size > 0);
  const itemCount = computed(
    () => toValue(reactiveState.filteredItems)?.length || 0
  );

  /**
   * Initialize the adapter
   */
  async function initialize(params?: BrowserContext): Promise<void> {
    try {
      await adapterRef.value.initialize(params);
      isInitialized.value = true;
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to initialize adapter';
      if (reactiveState.error) {
        reactiveState.error.value = errorMsg;
      }
      throw err;
    }
  }

  /**
   * Refresh items from the adapter
   */
  async function refresh(): Promise<void> {
    try {
      // Re-initialize to refresh data
      await adapterRef.value.initialize();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to refresh';
      if (reactiveState.error) {
        reactiveState.error.value = errorMsg;
      }
      throw err;
    }
  }

  /**
   * Perform search
   */
  function search(query: string): void {
    adapterRef.value.setSearchQuery(query);
  }

  /**
   * Select a single item
   */
  function selectItem(item: T): void {
    adapterRef.value.selectItem(item);
  }

  /**
   * Toggle item selection
   */
  function toggleItem(item: T): void {
    adapterRef.value.toggleItem(item);
  }

  /**
   * Clear all selections
   */
  function clearSelection(): void {
    adapterRef.value.clearSelection();
  }

  /**
   * Select all items
   */
  function selectAll(): void {
    // Select all filtered items
    const filteredItems = toValue(reactiveState.filteredItems);
    if (filteredItems) {
      filteredItems.forEach((item) => {
        adapterRef.value.selectItem(item);
      });
    }
  }

  /**
   * Set selected IDs
   * Intelligently syncs selection state - doesn't clear items not in current view
   */
  function setSelectedIds(ids: string[] | Set<string>): void {
    // Convert to Set for efficient lookup
    const targetIds = new Set(Array.isArray(ids) ? ids : ids);
    const currentIds = selectedIds.value;

    // Find IDs to add (in target but not currently selected)
    const toAdd = new Set<string>();
    targetIds.forEach((id) => {
      if (!currentIds.has(id)) {
        toAdd.add(id);
      }
    });

    // Find IDs to remove (currently selected but not in target)
    const toRemove = new Set<string>();
    currentIds.forEach((id) => {
      if (!targetIds.has(id)) {
        toRemove.add(id);
      }
    });

    // Add new selections from current items
    const items = toValue(reactiveState.items);
    if (items && toAdd.size > 0) {
      items.forEach((item) => {
        const itemId = adapterRef.value.itemConfig.getId(item);
        if (toAdd.has(itemId)) {
          adapterRef.value.selectItem(item);
        }
      });
    }

    // Remove deselected items (these might not be in current view, but adapter tracks them)
    if (toRemove.size > 0) {
      const selectedItems = toValue(reactiveState.selectedItems);
      if (selectedItems) {
        selectedItems.forEach((item) => {
          const itemId = adapterRef.value.itemConfig.getId(item);
          if (toRemove.has(itemId)) {
            adapterRef.value.toggleItem(item); // Toggle to remove
          }
        });
      }
    }
  }

  /**
   * Dispose of the adapter and clean up
   */
  function dispose(): void {
    // Clear initialization state
    isInitialized.value = false;
    // The adapter's reactive state will be garbage collected when the adapter is destroyed
  }

  // Auto-initialize if requested
  if (autoInitialize) {
    initialize().catch(() => {
      // Error is already stored in state.error
    });
  }

  // Clean up on unmount
  onUnmounted(() => {
    dispose();
  });

  return {
    // State refs from adapter's reactive state
    items: reactiveState.items,
    filteredItems: reactiveState.filteredItems,
    selectedItems: reactiveState.selectedItems,
    focusedItem: reactiveState.focusedItem,
    searchQuery: reactiveState.searchQuery,
    isLoading: reactiveState.isLoading,
    isInitialized,
    error: reactiveState.error,
    hasMore: reactiveState.hasMore,
    page: reactiveState.page,

    // Computed
    selectedIds,
    hasSelection,
    itemCount,

    // Methods
    initialize,
    refresh,
    search,
    selectItem,
    toggleItem,
    clearSelection,
    selectAll,
    setSelectedIds,
    dispose,
  };
}

/**
 * Create a browser adapter with reactive state management
 *
 * This is a convenience function that creates both the adapter and
 * the reactive wrapper in one call.
 *
 * @param AdapterClass - The adapter class constructor
 * @param itemConfig - Item configuration for the adapter
 * @param context - Initial browser context
 * @param autoInitialize - Whether to auto-initialize
 * @returns Reactive adapter state and the adapter instance
 */
export function createReactiveBrowserAdapter<T>(
  AdapterClass: new (...args: unknown[]) => BaseBrowserAdapter<T>,
  itemConfig: unknown,
  context?: Partial<BrowserContext>,
  autoInitialize = true
): {
  adapter: BaseBrowserAdapter<T>;
  state: ReactiveAdapterState<T>;
} {
  const adapter = new AdapterClass(itemConfig, context);
  const state = useBrowserAdapter(adapter, autoInitialize);

  return { adapter, state };
}
